import { clsx } from 'clsx';
import { RefreshCw, LineChart } from 'lucide-react';
import { Card, CardHeader } from '../../components/common/Card';
import { HistoricalChart } from '../../components/charts/HistoricalChart';
import { StockPriceChart } from '../../components/charts/ComparisonChart';
import type { EnhancedAnalysis } from '../../types';
import type { HistoryDataPoint, StockPriceData } from '../../utils/api';

interface TechnicalsTabProps {
  analysis: EnhancedAnalysis;
  additionalPriceData: StockPriceData | null;
  historyData: HistoryDataPoint[];
  historyDays: number;
  loadingHistory: boolean;
  onTimeRangeChange: (days: number) => void;
}

export function TechnicalsTab({
  analysis,
  additionalPriceData,
  historyData,
  historyDays,
  loadingHistory,
  onTimeRangeChange,
}: TechnicalsTabProps) {
  return (
    <div className="space-y-6">
      {/* Stock Price Chart - Always reserve space to prevent layout shift */}
      <Card className="p-4">
        {(additionalPriceData?.daily_prices?.length || analysis.price_history?.length) ? (
          <StockPriceChart
            data={additionalPriceData?.daily_prices || analysis.price_history || []}
            dailyData={additionalPriceData?.daily_prices || analysis.price_history || []}
            ticker={analysis.ticker}
          />
        ) : (
          <div className="h-80 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
            <RefreshCw className="h-8 w-8 animate-spin mb-4" />
            <p>Loading price data...</p>
          </div>
        )}
      </Card>

      {/* Historical Trends */}
      <Card className="relative">
        <CardHeader
          title="Historical Trends"
          subtitle="Sentiment and disconnect over time"
          icon={<LineChart className="h-5 w-5" />}
        />

        <div className="space-y-4">
          {/* Time Range Selector */}
          <div className="flex items-center gap-2">
            {[
              { days: 7, label: '7D' },
              { days: 30, label: '30D' },
              { days: 90, label: '90D' },
            ].map(({ days, label }) => (
              <button
                key={days}
                onClick={() => onTimeRangeChange(days)}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-all',
                  historyDays === days
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Chart */}
          {loadingHistory ? (
            <div className="h-80" />
          ) : historyData.length > 0 ? (
            <>
              <HistoricalChart
                data={historyData}
                showSentiment={true}
                showDisconnect={true}
                showPrice={true}
              />

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <span className="text-[var(--color-text-muted)]">Sentiment</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500" />
                  <span className="text-[var(--color-text-muted)]">Disconnect</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-[var(--color-text-muted)]">Price</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
              <LineChart className="h-12 w-12 mb-4 opacity-50" />
              <p>No historical data available</p>
              <p className="text-sm mt-1">History will be populated after tracking begins</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
